/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

// What the survey submission widget may claim from the answers the page holds:
// the pre-submit status it displays and offers Submit for, and whether the page
// holds answer values the server does not have yet.
//
// Dependency-free on purpose: the unit test beside it evaluates these rules
// without the build, and the widget and SurveyComponent both read them.

// Pre-submit statuses — a submission sitting in one of these was either never
// submitted or was stepped back from review. Gating that means "not yet locked
// by review" (e.g. the survey widget) shares this one set.
export const PRE_SUBMIT_STATUS_CODES = new Set(['not-started', 'in-progress', 'ready-to-submit']);

// The value-bearing answer fields, mirroring the server's change detection —
// see $comparisonFields in SurveyService::surveyAnswerHasChanged() (parallel logic:
// keep the two lists in sync). Everything else on an answer row is identity or
// server-owned metadata and never represents user intent.
export const ANSWER_VALUE_FIELDS = ['valueText', 'valueNumeric', 'valueBool', 'valueDate', 'locationId', 'stakeholderId', 'fileUploadId'];

// Loaded rows carry DB-typed values (DECIMAL as "2472.000000", tinyint as 1) while
// inputs write JS-typed ones (2472, 1) — normalize so type-equivalent values compare
// equal and don't misread as user edits. Empty string stays distinct from null (the
// system convention for string fields — clearing a text answer is a real edit) and
// must not fall into the numeric branch, where it would coerce to "0". valueText
// never numerically coerces at all: it's free text, so "007" vs "7" is a real
// edit — coercing them equal would hide it from dirty-marking and let a conflict
// reload-merge drop it.
export const normalizeAnswerValue = (value, field = null) => {
    if (value == null) {
        return null;
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (field !== 'valueText' && typeof value === 'string' && value.trim() !== '' && !isNaN(value)) {
        return String(Number(value));
    }
    return String(value);
};

export const answerValuesDiffer = (a, b) =>
    ANSWER_VALUE_FIELDS.some((field) => normalizeAnswerValue(a?.[field], field) !== normalizeAnswerValue(b?.[field], field));

/**
 * True when the answers the page holds carry a value the server does not have:
 * any page row whose values differ from the saved row of the same question code.
 *
 * Only the page's rows are compared. A saved row the page no longer holds was
 * dropped by the visibility filter (its onlyWhen parent changed), not typed away
 * in view mode, and a deliberate delete only happens in edit mode, where the
 * widget offers no Submit anyway. An input that writes an all-null row on load
 * for a question nobody answered (the contact input does, when no project contact
 * holds the relationship) compares equal to the absent saved row, so that is not
 * an unsaved change either. This is deliberately NOT the dirty-code tracking in
 * SurveyComponent: that set excludes load-time writes so a conflict merge can't
 * replay them, and the load-time write is exactly the case this has to catch.
 */
export const hasUnsavedAnswerChanges = (savedAnswers, answers) => {
    const savedByCode = new Map((savedAnswers ?? []).map((answer) => [String(answer.code), answer]));
    return (answers ?? []).some((answer) => answerValuesDiffer(savedByCode.get(String(answer.code)), answer));
};

/**
 * The status the stepper displays and the action panel offers, as
 * { displayStatusCode, displayStatusIdx }.
 *
 * Recomputes a pre-submit status from section completion, so a stored status row
 * that went stale can catch up: the row is only refreshed on answer save, so it
 * sits at in-progress when the survey template changed underneath it and nothing
 * is outstanding any more. The submit endpoint recomputes the same way before it
 * gates, which is what makes offering Submit on the recomputed status safe.
 *
 * The caller must count completion over the answers the server HOLDS — the last
 * saved answers — never the page's unsaved state. The endpoint only sees stored
 * answers, so a status recomputed from the page can claim what a submit would
 * refuse: a contact question auto-selects a project contact on load, which read as
 * complete in the browser while the stored answer was still empty, and the widget
 * offered a Submit that the server then rejected. The live section counter is
 * where unsaved edits show.
 *
 * Only applied to pre-submit states — once the survey is locked (in-review /
 * review-complete) the server status is authoritative. And the recompute never
 * drops below the status the server already persisted: the local code is derived
 * from fully-complete SECTIONS, while the server records status by REQUIRED
 * QUESTIONS answered — so answering one question in a multi-question section
 * leaves the local code at 'not-started' even though the server has already
 * saved 'in-progress'. Floor the pre-submit display at the server status so the
 * display never regresses below saved truth.
 *
 *   indexOf — the ordinal resolver from useOrderedSubmissionStatuses.
 */
export const deriveDisplayStatus = ({ currentStatusCode, hasSubmissionRecord, completedCount, totalCount, indexOf }) => {
    if (!PRE_SUBMIT_STATUS_CODES.has(currentStatusCode)) {
        return { displayStatusCode: currentStatusCode, displayStatusIdx: indexOf(currentStatusCode) };
    }

    const localPreSubmitCode = !hasSubmissionRecord || totalCount === 0
        ? currentStatusCode  // no record yet or no required sections: defer to server
        : completedCount === totalCount ? 'ready-to-submit'
        : completedCount > 0 ? 'in-progress'
        : 'not-started';

    const displayStatusCode = indexOf(localPreSubmitCode) >= indexOf(currentStatusCode)
        ? localPreSubmitCode
        : currentStatusCode;

    return { displayStatusCode, displayStatusIdx: indexOf(displayStatusCode) };
};

/**
 * Everything the submission widget shows about a pre-submit survey, from the two
 * answer sets the page holds:
 *
 *   completedCount / totalCount — the live progress counter, over the page's answers
 *   displayStatusCode / Idx     — the stepper position and the action offered, over
 *                                 the SAVED answers (see deriveDisplayStatus)
 *   hasUnsavedChanges           — the page holds values the server does not
 *
 *   requiredSections  — the above-boundary sections, in order
 *   isSectionComplete — (section, answers) => bool, the page's completion rule
 */
export const deriveSubmissionDisplay = ({
    currentStatusCode, hasSubmissionRecord, requiredSections, isSectionComplete, answers, savedAnswers, indexOf,
}) => {
    const completedCount = requiredSections.filter((section) => isSectionComplete(section, answers)).length;
    const savedCompletedCount = requiredSections.filter((section) => isSectionComplete(section, savedAnswers)).length;
    const totalCount = requiredSections.length;

    return {
        completedCount,
        totalCount,
        hasUnsavedChanges: hasUnsavedAnswerChanges(savedAnswers, answers),
        ...deriveDisplayStatus({
            currentStatusCode,
            hasSubmissionRecord,
            completedCount: savedCompletedCount,
            totalCount,
            indexOf,
        }),
    };
};

/**
 * What the widget's action panel offers in view mode for a pre-submit survey, as
 * { showProgress, editAction, submitAction, showUnsavedHint, showPrerequisiteNudge }:
 *
 *   showProgress    — the "n/m Required Sections Completed" counter
 *   editAction      — 'start' | 'continue' | 'edit' | null, the button that opens
 *                     edit mode, worded for the displayed status
 *   submitAction    — 'submit' (Submit for Review) | 'review' (disabled "Review
 *                     changes": the page holds values the server lacks, so the
 *                     editor must save first) | null
 *   showUnsavedHint — the one-line explanation of why the page differs from the
 *                     last save
 *   showPrerequisiteNudge — "complete the previous phase first" with its link, in
 *                     place of everything above (prerequisiteBlocked: the earlier
 *                     phase's survey is not yet review-complete and the viewer is
 *                     bound by that order)
 *
 * A viewer who cannot edit (canEdit false: no update right, no edit grant on the
 * CA's context node, or the survey is locked) gets none of it — the stepper
 * only. Not the prerequisite nudge, which is an instruction to go and fill in a
 * survey; and not the counter, which is a working aid for whoever fills the
 * survey and, for a viewer whose data is restricted (an auditor cannot see
 * answers to questions hidden from auditors), counts over answers they do not
 * hold and contradicts the stepper. Submitting is an editor's act: the server's
 * submit gate checks only the survey-data permission and CA visibility, never
 * the node edit grant, so the widget must not offer a Submit the page's own
 * edit test would refuse.
 *
 * At not-started nothing has been saved yet, so "differs from the last save" would
 * describe a save that never happened: the Start Survey button is the whole story
 * there, and the hint waits for in-progress.
 *
 * Locked statuses (in-review / review-complete) have their own panel and return
 * nothing here.
 */
export const deriveSubmissionActions = ({ displayStatusCode, hasUnsavedChanges, canEdit, prerequisiteBlocked = false }) => {
    const none = { showProgress: false, editAction: null, submitAction: null, showUnsavedHint: false, showPrerequisiteNudge: false };
    if (!canEdit || !PRE_SUBMIT_STATUS_CODES.has(displayStatusCode)) {
        return none;
    }
    if (prerequisiteBlocked) {
        return { ...none, showProgress: true, showPrerequisiteNudge: true };
    }

    const ready = displayStatusCode === 'ready-to-submit';
    const notStarted = displayStatusCode === 'not-started';

    return {
        showProgress: true,
        editAction: ready ? 'edit' : notStarted ? 'start' : 'continue',
        submitAction: !ready ? null : hasUnsavedChanges ? 'review' : 'submit',
        showUnsavedHint: !!hasUnsavedChanges && !notStarted,
        showPrerequisiteNudge: false,
    };
};

/**
 * Whether the widget's action column has anything to show, so an empty one can be
 * left out and the stepper take the card instead of sitting beside an empty
 * bordered box. In edit mode the column holds only the progress counter, for a
 * pre-submit survey with required sections. In view mode it holds whatever
 * deriveSubmissionActions offers (the counter only counts when there are
 * sections to count), or the locked panel at in-review / review-complete, which
 * always has a line to say.
 *
 *   actions — the result of deriveSubmissionActions for this view
 */
export const hasSubmissionActionPanel = ({ editMode, currentStatusCode, totalCount, actions }) => {
    if (editMode) {
        return PRE_SUBMIT_STATUS_CODES.has(currentStatusCode) && totalCount > 0;
    }
    const { showProgress, editAction, submitAction, showUnsavedHint, showPrerequisiteNudge } = actions;
    return (showProgress && totalCount > 0)
        || showPrerequisiteNudge
        || editAction != null
        || submitAction != null
        || showUnsavedHint
        || currentStatusCode === 'in-review'
        || currentStatusCode === 'review-complete';
};
